import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Drawer from "@intility/bifrost-react/Drawer";
import Grid from "@intility/bifrost-react/Grid";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Modal from "@intility/bifrost-react/Modal";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import { useEffect, useMemo, useState } from "react";
import {
  createResourceType,
  deleteResourceType,
  listResourceTypes,
  updateResourceType,
} from "~/api/client";
import type { CreateResourceTypeRequest, ResourceType } from "~/api/fga-types";
import { useCapabilities, useEditableResourceTypes } from "~/auth/capabilities";
import {
  deriveSegmentTemplate,
  extractPlaceholderName,
  literalValue,
} from "~/lib/resourceType";
import { PageLayout } from "../../components/layout/PageLayout";
import ResourceTypeCreateForm from "./ResourceTypeCreateForm";

export default function ResourceCatalog() {
  const { can } = useCapabilities();
  const canCreate = can("vocabulary.create");
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const { canEdit } = useEditableResourceTypes(types.map((t) => t.slug));

  const [showCreate, setShowCreate] = useState(false);

  const [editingType, setEditingType] = useState<ResourceType | null>(null);

  const [deletingType, setDeletingType] = useState<ResourceType | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchTypes = () => {
    listResourceTypes()
      .then((res) => setTypes(res.items))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const reload = () => {
    setLoading(true);
    fetchTypes();
  };

  useEffect(fetchTypes, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return types;
    return types.filter(
      (t) =>
        t.slug.toLowerCase().includes(q) ||
        t.displayName.toLowerCase().includes(q),
    );
  }, [types, search]);

  const openEdit = (t: ResourceType) => {
    setEditingType(t);
  };

  const closeEdit = () => {
    setEditingType(null);
  };

  const handleEditSubmit = async (req: CreateResourceTypeRequest) => {
    if (!editingType) return;
    await updateResourceType(editingType.id, {
      displayName: req.displayName,
      description: req.description,
      nameRegex: req.nameRegex,
    });
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteResourceType(deletingType.id);
      setDeletingType(null);
      reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) return <p className="bf-p">Loading resource types…</p>;
  if (error) return <p className="bf-p bfc-alert">Error: {error}</p>;

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Blueprint resources" },
      ]}
      title="Blueprint resources"
      description={
        <>
          Shared vocabulary of resource kinds used in blueprints (e.g.{" "}
          <code>location</code>, <code>module</code>). These define the types of
          resources that can exist under a platform.
        </>
      }
      actions={
        canCreate ? (
          <Button small onClick={() => setShowCreate(true)}>
            Create Type
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <Input
          label="Search"
          placeholder="Filter by slug or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />

        <Section>
          <Table noBorder>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Slug</Table.HeaderCell>
                <Table.HeaderCell>Display name</Table.HeaderCell>
                <Table.HeaderCell>Kind</Table.HeaderCell>
                <Table.HeaderCell>Variable name</Table.HeaderCell>
                <Table.HeaderCell>Path segment</Table.HeaderCell>
                <Table.HeaderCell>Created</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filtered.length === 0 ? (
                <Table.Row>
                  <Table.Cell>None</Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                </Table.Row>
              ) : (
                filtered.map((t) => (
                  <Table.Row key={t.id}>
                    <Table.Cell>
                      <code>{t.slug}</code>
                    </Table.Cell>
                    <Table.Cell>{t.displayName}</Table.Cell>
                    <Table.Cell>
                      {t.isStatic ? (
                        <Badge state="neutral">Static</Badge>
                      ) : (
                        <Badge state="chill">Dynamic</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {extractPlaceholderName(t.nameRegex) ? (
                        <code>{extractPlaceholderName(t.nameRegex)}</code>
                      ) : (
                        "—"
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {t.isStatic && t.nameRegex ? (
                        <code>{literalValue(t.nameRegex)}</code>
                      ) : t.nameRegex &&
                        deriveSegmentTemplate(t.nameRegex, true) ? (
                        <code>{deriveSegmentTemplate(t.nameRegex, true)}</code>
                      ) : t.nameRegex ? (
                        <code>{t.nameRegex}</code>
                      ) : (
                        "—"
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(t.createdAt).toLocaleDateString()}
                    </Table.Cell>
                    <Table.Cell>
                      {canEdit(t.slug) && (
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button
                            small
                            variant="flat"
                            onClick={() => openEdit(t)}
                          >
                            Edit
                          </Button>
                          <Button
                            small
                            variant="flat"
                            state="alert"
                            onClick={() => setDeletingType(t)}
                            title="Delete (refuses if usages exist)"
                          >
                            ×
                          </Button>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </Section>
      </div>

      <Drawer
        header="Create resource type"
        isOpen={showCreate}
        onRequestClose={() => setShowCreate(false)}
      >
        {showCreate && (
          <ResourceTypeCreateForm
            onSubmit={(req) => createResourceType(req)}
            onSuccess={() => {
              setShowCreate(false);
              reload();
            }}
            submitLabel="Create"
            showDescription
            verbose
          />
        )}
      </Drawer>

      <Drawer
        header={editingType ? `Edit ${editingType.slug}` : "Edit"}
        isOpen={editingType !== null}
        onRequestClose={closeEdit}
      >
        {editingType && (
          <ResourceTypeCreateForm
            editType={editingType}
            key={editingType.id}
            onSubmit={handleEditSubmit}
            onSuccess={() => {
              closeEdit();
              reload();
            }}
            showDescription
            verbose
          />
        )}
      </Drawer>

      <Modal
        isOpen={deletingType !== null}
        onRequestClose={() => {
          if (deleteBusy) return;
          setDeletingType(null);
          setDeleteError(null);
        }}
        header="Delete resource type"
        width={480}
      >
        {deletingType && (
          <Grid gap={16}>
            <p className="bf-p" style={{ margin: 0 }}>
              This permanently deletes the resource type{" "}
              <strong>{deletingType.slug}</strong>. This will be refused if any
              blueprint placements reference it.
            </p>
            {deleteError && (
              <Message state="alert" header="Delete failed">
                {deleteError}
              </Message>
            )}
            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <Button
                variant="flat"
                onClick={
                  deleteBusy
                    ? undefined
                    : () => {
                        setDeletingType(null);
                        setDeleteError(null);
                      }
                }
                state={deleteBusy ? "inactive" : undefined}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                state={deleteBusy ? "inactive" : "alert"}
                onClick={deleteBusy ? undefined : handleDelete}
              >
                {deleteBusy ? "Deleting…" : "Delete type"}
              </Button>
            </div>
          </Grid>
        )}
      </Modal>
    </PageLayout>
  );
}
